// React, Next
import { ReactElement, useContext, useState } from 'react'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'

// MUI
import Box from '@mui/material/Box'
import AirIcon from '@mui/icons-material/Air'
import SensorsIcon from '@mui/icons-material/Sensors'
import ShieldIcon from '@mui/icons-material/Shield'
import SchoolIcon from '@mui/icons-material/School'
import BuildCircleIcon from '@mui/icons-material/BuildCircle'
import ArrowRightIcon from '@mui/icons-material/ArrowRight'
import ApartmentIcon from '@mui/icons-material/Apartment'
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates'
import EmailIcon from '@mui/icons-material/Email'

// Custom
import { GlobalContext } from 'context/GlobalContextProvider'
import { PageType, translateHref } from 'assets/functions/links'

// Style
import breakpoints from '@/assets/theme/breakpoints'
import classes from './MainMenu-CSS'
import themeColors from '@/assets/theme/colors'

type Props = {
  locale: string | string[]
}

/**
 * An entry either links somewhere (`href`) or groups further entries (`sub`),
 * which open in a panel to the right of the one holding them.
 */
type MenuItem = {
  name: string
  icon: ReactElement
  href?: PageType
  sub?: MenuItem[]
}

function SubMenuRow(props: { item: MenuItem; locale: string | string[] }) {
  const [open, setOpen] = useState(false)

  const { item, locale } = props

  // The anchor wraps the whole row, not just the label, so the padding and
  // the icon are part of the click target rather than dead space beside it.
  if (!item.sub)
    return (
      <Link
        href={translateHref(locale, item.href as PageType)}
        style={classes.subMenuLink}
      >
        <Box sx={classes.subMenuLinkBox}>
          {item.icon}
          {item.name}
        </Box>
      </Link>
    )

  return (
    <Box
      sx={{ position: 'relative' }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <Box sx={classes.subMenuLinkBox}>
        {item.icon}
        <Box component="span" style={classes.typoLink}>
          {item.name}
        </Box>
        {/* Filled triangle: the row opens a panel rather than a page. The
            glyph carries its own whitespace, so it needs no extra gap. */}
        <ArrowRightIcon
          sx={{
            marginLeft: 'auto',
            fontSize: '1.4rem',
            color: themeColors.gray.mid
          }}
        />
      </Box>

      {open && <SubMenu items={item.sub} locale={locale} nested />}
    </Box>
  )
}

function SubMenu(props: {
  items: MenuItem[]
  locale: string | string[]
  nested?: boolean
}) {
  return (
    <Box sx={props.nested ? classes.nestedSubMenuBox : classes.subMenuBox}>
      {props.items.map((item, idx) => (
        <SubMenuRow key={idx} item={item} locale={props.locale} />
      ))}
    </Box>
  )
}

export default function MainMenu(props: Props) {
  const { screenDimensions } = useContext(GlobalContext)

  const { t } = useTranslation('common')

  const [solutionsHover, setSolutionsHover] = useState(false)
  const [serviceHover, setServiceHover] = useState(false)
  const [companyHover, setCompanyHover] = useState(false)

  const iconStyle = {
    color: themeColors.bg.start,
    marginRight: '10px'
  }

  const subSolutions: MenuItem[] = [
    // A group rather than a page: further monitoring subjects land here.
    {
      name: t('subMenu.solutionsGroup1'),
      icon: <SensorsIcon style={iconStyle} />,
      sub: [
        {
          name: t('subMenu.solutions4'),
          href: 'iot-air-quality-monitoring',
          icon: <AirIcon style={iconStyle} />
        }
      ]
    }
  ]

  const subServices: MenuItem[] = [
    {
      name: t('subMenu.services6'),
      href: 'ot-cybersecurity',
      icon: <ShieldIcon style={iconStyle} />
    },
    {
      name: t('subMenu.services7'),
      href: 'ot-awareness-training',
      icon: <SchoolIcon style={iconStyle} />
    },
    {
      name: t('subMenu.services8'),
      href: 'test-equipment-modernization',
      icon: <BuildCircleIcon style={iconStyle} />
    }
  ]

  const subCompany: MenuItem[] = [
    {
      name: t('subMenu.company1'),
      href: 'technology',
      icon: <TipsAndUpdatesIcon style={iconStyle} />
    },
    {
      name: t('subMenu.company3'),
      href: 'about',
      icon: <ApartmentIcon style={iconStyle} />
    },
    {
      name: t('subMenu.company2'),
      href: 'contact',
      icon: <EmailIcon style={iconStyle} />
    }
  ]

  const getContactMarginRight = () => {
    if (screenDimensions.width > breakpoints.desktopMid) return '100px'
    if (screenDimensions.width > breakpoints.desktopSmall) return '50px'
    return '30px'
  }

  const renderSubMenu = (subMenu: MenuItem[]) => {
    // An empty group would otherwise drop a blank white panel on hover.
    if (subMenu.length === 0) return null
    return <SubMenu items={subMenu} locale={props.locale} />
  }

  return (
    <>
      <Box
        sx={{ position: 'relative' }}
        onMouseEnter={() => setSolutionsHover(true)}
        onMouseLeave={() => setSolutionsHover(false)}
      >
        <Link
          href={translateHref(props.locale, 'solutions')}
          style={classes.typoLink}
        >
          <Box
            sx={classes.linkBox}
            style={{
              paddingLeft:
                screenDimensions.width > breakpoints.desktopMid
                  ? '40px'
                  : '20px',
              paddingRight:
                screenDimensions.width > breakpoints.desktopMid
                  ? '40px'
                  : '20px'
            }}
          >
            <Box sx={classes.upperBox}>{t('menu.solutions')}</Box>
            <Box
              sx={classes.underBox}
              style={{
                opacity: !solutionsHover ? 0 : 1
              }}
            />
          </Box>
        </Link>
        {solutionsHover && renderSubMenu(subSolutions)}
      </Box>

      <Box
        sx={{ position: 'relative' }}
        onMouseEnter={() => setServiceHover(true)}
        onMouseLeave={() => setServiceHover(false)}
      >
        <Link
          href={translateHref(props.locale, 'services')}
          style={classes.typoLink}
        >
          <Box
            sx={classes.linkBox}
            style={{
              paddingLeft:
                screenDimensions.width > breakpoints.desktopMid
                  ? '40px'
                  : '20px',
              paddingRight:
                screenDimensions.width > breakpoints.desktopMid
                  ? '40px'
                  : '20px'
            }}
          >
            <Box sx={classes.upperBox}>{t('menu.services')}</Box>
            <Box
              sx={classes.underBox}
              style={{
                opacity: !serviceHover ? 0 : 1
              }}
            />
          </Box>
        </Link>
        {serviceHover && renderSubMenu(subServices)}
      </Box>

      <Box
        sx={{ position: 'relative', marginRight: getContactMarginRight() }}
        onMouseEnter={() => setCompanyHover(true)}
        onMouseLeave={() => setCompanyHover(false)}
      >
        <Link
          href={translateHref(props.locale, 'technology')}
          style={classes.typoLink}
        >
          <Box
            sx={classes.linkBox}
            style={{
              paddingLeft:
                screenDimensions.width > breakpoints.desktopMid
                  ? '40px'
                  : '20px',
              paddingRight:
                screenDimensions.width > breakpoints.desktopMid
                  ? '40px'
                  : '20px'
            }}
          >
            <Box sx={classes.upperBox}>{t('menu.company')}</Box>
            <Box
              sx={classes.underBox}
              style={{
                opacity: !companyHover ? 0 : 1
              }}
            />
          </Box>
        </Link>
        {companyHover && renderSubMenu(subCompany)}
      </Box>
    </>
  )
}
